/**
 * hs-lead-submit.js — Trio Sporting quote/enquiry/brochure form submission.
 *
 * ⚠ TRANSPORT CHANGED 2026-08-02 (Luca, pre-go-live forms→CRM verification): this script no
 * longer calls HubSpot's client-side "formsnext" endpoint directly. It now POSTs JSON to
 * trio-member-app's /api/lead-submit, which calls HubSpot's documented, authenticated CRM
 * Contacts API server-side. See LEAD SUBMIT TRANSPORT below for the full story — the short
 * version: a systematic internal-API audit found that the old client-side endpoint always
 * returned HTTP 200 accepted:true while silently creating ZERO contacts for ~20 real submissions
 * today, including genuine Chromium (headless AND headed, webdriver masked, real hutk cookie
 * included) — not just the already-known curl/no-browser case. The new server-side path was
 * verified working on the first try (201 Created, real contact ID) and is not subject to any of
 * that endpoint's undocumented anti-bot ambiguity. The PREREQUISITE CALLS / INTERCEPT POINT
 * sections below are kept as historical record (the click-intercept mechanism they justify is
 * still needed; the specific HubSpot embed-widget network sequence they describe is not fired by
 * this version of the script — the fireHsPrereqCalls()/parseHsResponse() code that implemented
 * that sequence has been removed (dead code once the transport changed), kept here in prose only
 * for anyone debugging why the old approach looked like it worked in isolated tests but didn't in
 * practice).
 *
 * WHY THIS EXISTS (2026-07-24 QA finding):
 * The theme's contact/enquiry forms use Shopify's native `{% form 'contact' %}`, which only
 * sends an email notification — Shopify does NOT expose a webhook for contact-form submissions
 * (confirmed: no such topic exists in this store's registered webhooks). The code comments that
 * previously claimed "an existing Make.com scenario watches the contact form" were an unverified
 * assumption (also flagged `[verify]` in shopify-theme/README.md) and do NOT reflect reality —
 * today these forms reach no CRM automation at all. This script fixes that by submitting straight
 * to HubSpot (no server, no exposed CRM token, no added cost).
 *
 * LIVE WIRING (2026-07-24, form published by CEO in HubSpot):
 *   Portal:  147912925 (EU hublet — app-eu1.hubspot.com)
 *   Form:    "Website Quote Requests", GUID 53a4d455-11fb-4da8-910a-b2ea2b6442a8
 *
 * ⚠ SUBMISSION ENDPOINT — NOT the classic documented one, verified empirically (2026-07-24):
 *   This form was built in HubSpot's current form editor, which produces a "next-gen" form
 *   (internally: type 0 / step-based "modules" tree — HubSpot calls this "formsnext"). The
 *   classic public, documented endpoint (`api-eu1.hsforms.com/submissions/v3/integration/
 *   submit/{portal}/{guid}`, JSON body, plain property names like "firstname") was tried
 *   FIRST, exactly as HubSpot's docs describe it — it returns a clean HTTP 200 with an empty
 *   body, giving every appearance of success, but silently creates NO contact (confirmed with
 *   two independent read paths, waited >90s for indexing). It is simply not wired to formsnext
 *   forms on this portal. This is undocumented behaviour discovered by capturing the network
 *   traffic of HubSpot's own embedded widget actually submitting this exact form, which uses:
 *
 *     POST https://forms-eu1.hsforms.com/submissions/v3/public/submit/formsnext/multipart/{portal}/{guid}
 *     Content-Type: multipart/form-data
 *     fields named by the form's internal propertyReference, NOT the plain property name
 *     (e.g. "0-1/firstname", not "firstname" — see FIELD MAP below), plus an "hs_context" part.
 *
 *   Confirmed end-to-end (2026-07-24): a real-browser `fetch()` POST in this exact shape,
 *   fired cold from this theme's own deployed page (no HubSpot script preloaded), returned
 *   `accepted:true` and created/updated a real CRM contact. A raw, cookie-less script POST
 *   (curl/urllib, no real browser engine) to the SAME endpoint also returns `accepted:true`
 *   but does NOT create a contact — HubSpot silently drops non-browser traffic without
 *   surfacing an error. This is consistent with normal client-side use (real users only) and
 *   does not affect real visitors submitting from their browser.
 *
 *   FIELD MAP (propertyReference, fetched from https://forms-eu1.hsforms.com/embed/v4/
 *   render-definition/147912925/<guid> — re-verify this map if the form is ever rebuilt from
 *   scratch in HubSpot; a GUID always changes on rebuild too, so the section setting would need
 *   updating either way):
 *     First Name   "0-1/firstname"
 *     Last Name    "0-1/lastname"
 *     Email        "0-1/email"   (required on the form)
 *     Mobile Phone "0-1/mobilephone"   <-- NOT "phone". Field type is "phoneNumber" bound to
 *                  the "mobilephone" contact property. Must be a validly-FORMATTED phone
 *                  number (E.164-ish, e.g. "+966501234567") — HubSpot rejects the submission
 *                  (400, INVALID_FORMATTED_PHONE_FORMAT) if it looks malformed (e.g. all-zero
 *                  test numbers). Spaces are stripped before sending to reduce that risk.
 *     Company Name "0-1/company"
 *     Message      "0-1/message"   (multi-line)
 *   The form has NO campaign_interest / source_channel fields — sending them is pointless (this
 *   endpoint keys only off propertyReference values that exist in the form definition) and
 *   earlier drafts of this script did so; removed. Service/source/group-size/date/free-text are
 *   folded into the "message" property instead (see buildMessage below), prefixed
 *   "[Website — <service or page>]" so Sales still sees the full context.
 *
 * ⚠ PREREQUISITE CALLS — the multipart submit alone is NOT reliable (2026-07-24 finding):
 *   A "cold" multipart POST straight to the submit endpoint above always returns HTTP 200 /
 *   `accepted:true` — but reliably creates a CRM contact only the FIRST time; every subsequent
 *   cold submission (fresh, never-used test email, still `accepted:true`) silently created no
 *   contact, confirmed by waiting >2 minutes and reading back both by search and by direct ID.
 *   Reproducing HubSpot's own embedded-widget network sequence — GET .../embed/v4/visitor/
 *   {portal}/{guid}, GET .../embed/v4/feature-control/{portal}, one POST .../submissions-
 *   validation/v1/validate/{portal}/{guid} per field, POST .../emailcheck/v1/json-ext for the
 *   email — immediately BEFORE the multipart submit reliably creates the contact (confirmed
 *   twice, new contact visible within ~15s both times). Root cause unconfirmed (likely an
 *   anti-bot/anti-scraper heuristic that only trusts a submission preceded by this exact call
 *   pattern), but the fix is proven, so this script fires the same sequence every time. Adds
 *   ~4 small parallel requests (well under 1s) before the actual submit.
 *
 * ⚠ INTERCEPT POINT — button `click`, NOT form `submit` (2026-07-24 finding):
 *   This store has a platform-level Shopify listener bound at `window`/`document` (capture
 *   phase) that intercepts every native `submit` event site-wide (Shopify's storefront spam/
 *   bot-protection runtime — confirmed by hCaptcha challenge assets loading the moment a form
 *   is submitted). It runs BEFORE the event reaches the `<form>` element at all, so a listener
 *   registered with `form.addEventListener('submit', ...)` — the standard, obvious approach,
 *   and what this script originally did — never fires; traced with instrumented
 *   addEventListener/fetch across two independent test harnesses, confirming zero `submit`
 *   listeners on the form itself ever ran, on any browser-driven submission path. Intercepting
 *   the submit BUTTON's `click` event instead (capture phase, calling `preventDefault()`
 *   immediately) stops the default action before a `submit` event is even generated, sidestepping
 *   Shopify's interceptor entirely. Confirmed working end-to-end 2026-07-24.
 *
 * ACTIVATION: does nothing until a HubSpot Form GUID is configured on the section
 * (`data-hs-form-guid`). Until then, the form submits exactly as before (Shopify-native,
 * unwired) — so this script is safe to ship with zero behavioural change.
 *
 * LEAD SUBMIT TRANSPORT (2026-08-02, current): a single POST of JSON (email, firstname,
 * lastname, mobilephone, company, message) to trio-member-app's /api/lead-submit — same stable
 * prod alias as the brochure-email endpoint (trio-member-app.vercel.app). That endpoint creates
 * (or, for a returning visitor, updates) the HubSpot contact server-side via the CRM Contacts API
 * using a Service Key, and returns { ok: true, contactId } on success. The data-hs-portal /
 * data-hs-form-guid attributes are still read but are no longer sent anywhere — kept on every
 * section instance as a harmless label of which HubSpot form this used to map to, not removed
 * from the templates to avoid a churn-only change across 22 sections for zero behavioural gain.
 * data-hs-service / data-hs-source are still folded into the "message" property exactly as
 * before (see buildMessage below) — Sales still sees full per-page context.
 *
 * Required data-* attributes on a container element wrapping the <form> (e.g. <div
 * class="m-form" data-hs-form ...>) — kept off the <form> tag itself because Shopify's Liquid
 * `{% form %}` tag attribute parser does not reliably support hyphenated parameter keys:
 *   data-hs-form                 marks the container; must wrap exactly one <form>
 *   data-hs-portal                HubSpot Portal (Hub) ID
 *   data-hs-form-guid             HubSpot Form GUID — blank = fallback to native Shopify submit
 *   data-hs-service               human-readable service/campaign interest -> folded into message
 *   data-hs-source                page/enquiry source label -> folded into message + hs_context
 *   data-hs-message-property      internal HubSpot property name for composed notes (default "message")
 *   data-hs-download-url          optional (brochure forms): the brochure PDF URL. NEVER opened,
 *                                  linked, or shown on the page (changed 2026-08-02, Marcello
 *                                  "brochure via email" mandate — a free direct download let leads
 *                                  type throwaway emails). Instead it's sent as the payload of a
 *                                  second POST, straight after the HubSpot submit succeeds, to
 *                                  trio-member-app's /api/brochure-email — that endpoint emails
 *                                  the PDF link to whatever address the visitor typed. Forms
 *                                  without this attribute (every non-brochure lead form on the
 *                                  site — contact, enquire, competitions, ...) are completely
 *                                  unaffected: the email-send step only runs when this attribute
 *                                  is present and non-blank.
 *   data-hs-brochure-endpoint      optional override of the brochure-email API URL (defaults to
 *                                  BROCHURE_EMAIL_ENDPOINT below). Only meant for local/preview
 *                                  testing against a non-production member-app instance — no
 *                                  shipped template sets this.
 *
 * Recognised field names inside the form (kept identical to the existing Shopify contact-form
 * field names so the no-JS / no-GUID fallback keeps working unmodified):
 *   contact[name], contact[email], contact[Country code], contact[phone],
 *   contact[Organisation], contact[Group size], contact[Preferred date], contact[body]
 *
 * Success/error UI: toggles `[data-hs-success]` / `[data-hs-error]` elements inside the form
 * (both start `hidden`). The Liquid-rendered `form.posted_successfully?` / `form.errors` blocks
 * stay untouched as the native fallback for when this script never fires.
 */
(function () {
  'use strict';

  // trio-member-app's stable production alias (survives every deploy — see
  // member-app/.vercel/project.json, projectId prj_IngJWV00S2GQ3GKmt3ZqDlce1eOr, and the "prod
  // alias root" verification in this session's report). Both endpoints are unauthenticated-but-
  // validated (see each route's own header comment) and CORS-allowlisted for this site's origins.
  var LEAD_SUBMIT_ENDPOINT = 'https://trio-member-app.vercel.app/api/lead-submit';
  var BROCHURE_EMAIL_ENDPOINT = 'https://trio-member-app.vercel.app/api/brochure-email';

  function qs(form, name) {
    var el = form.querySelector('[name="' + name + '"]');
    return el ? el.value.trim() : '';
  }

  function splitName(full) {
    full = (full || '').trim();
    if (!full) return { first: '', last: '' };
    var parts = full.split(/\s+/);
    return { first: parts.shift(), last: parts.join(' ') };
  }

  function toggle(el, show) {
    if (el) el.hidden = !show;
  }

  // Merges the country-code <select> value with the digits typed into the phone <input>,
  // avoiding a duplicated country code when the visitor types it themselves despite the
  // separate selector sitting right next to the field. [2026-08-06, phone-required follow-up]
  // Now that the phone field is REQUIRED (see the `pattern` + `required` on the tel input in
  // m-enquire.liquid / m-brochure-form.liquid), every visitor fills it in, and a meaningful
  // share type the full number out of habit (with or without the leading "+", with or without
  // a local trunk "0"). Naively concatenating countryCode + phoneRaw in that case produced a
  // duplicated prefix (e.g. "+966" + "966501234567" -> "+966966501234567") — a
  // technically-well-formed-looking but unreachable number reaching HubSpot. Exactly the
  // "unreachable contact" problem this whole change exists to prevent.
  //
  // Handles, in priority order:
  //   1. Value already starts with "+" and the digits right after it match the selected
  //      country code -> de-duplicate: keep that country code once, drop it from the rest.
  //   2. Value starts with "+" but a DIFFERENT code than the one selected -> the visitor typed
  //      a complete international number for a country other than the one picked in the
  //      dropdown. Trusted as-is, untouched — overriding it with the selector's code would
  //      corrupt an already-correct number.
  //   3. No "+", but the value starts with the selected country's digits AND what's left looks
  //      like a plausible national number (6–12 digits) -> same de-duplication as (1).
  //   4. Otherwise -> a plain local number. Strips a single leading trunk "0" (standard when
  //      prefixing a country code, incl. Saudi/Gulf numbers) before prepending the selected
  //      country code.
  // Every branch falls back to the least destructive option when ambiguous or too short to be
  // confident about — this never invents digits and never collapses a number down to just the
  // bare country code. A number sent exactly as typed is always safer than one guessed wrong.
  function normalizePhone(countryCode, phoneRaw) {
    if (!phoneRaw) return '';
    // Same strip as before: keep a leading '+' if present, digits only otherwise.
    var raw = phoneRaw.replace(/(?!^\+)[^\d]/g, '');
    var cc = (countryCode || '').replace(/\D/g, '');

    if (raw.charAt(0) === '+') {
      var afterPlus = raw.slice(1);
      if (cc && afterPlus.indexOf(cc) === 0) {
        var deduped = afterPlus.slice(cc.length).replace(/^0/, '');
        return deduped ? ('+' + cc + deduped) : raw; // never collapse to just "+cc"
      }
      return raw; // different/undetected country code -> already a full number, leave alone
    }

    if (cc && raw.indexOf(cc) === 0) {
      var rest = raw.slice(cc.length);
      if (rest.length >= 6 && rest.length <= 12) {
        return '+' + cc + (rest.replace(/^0/, '') || rest);
      }
      // Left-over is too short/long to plausibly be a national number -> the leading digits
      // are more likely a coincidental overlap with a short local number than a real country
      // code match; fall through and treat the whole value as local instead of guessing.
    }

    var local = raw.replace(/^0/, '') || raw;
    return cc ? ('+' + cc + local) : raw;
  }

  // Folds everything the real form doesn't have a dedicated property for (service, source page,
  // group size, preferred date, free-text body) into the single "message" property, so Sales
  // still sees the full context even though the HubSpot form only exposes firstname/lastname/
  // email/mobilephone/company/message.
  function buildMessage(service, source, guests, date, body) {
    var label = service || source || 'Website enquiry';
    var lines = ['[Website — ' + label + ']'];
    if (source && source !== label) lines.push('Page: ' + source);
    if (guests) lines.push('Group size: ' + guests);
    if (date) lines.push('Preferred date: ' + date);
    if (body) lines.push('Message: ' + body);
    return lines.join('\n');
  }

  // Sends the composed contact fields as JSON to trio-member-app's /api/lead-submit, which
  // creates/updates the HubSpot contact server-side (see LEAD SUBMIT TRANSPORT in the header
  // comment). Rejects (unlike sendBrochureEmail below) because a failed lead submission IS the
  // exceptional case the caller's .catch must show an error state for — losing a lead silently
  // is exactly the bug this transport change exists to fix.
  function sendLeadToHubspot(fieldValues) {
    var body = {};
    fieldValues.forEach(function (f) { if (f.value) body[f.name] = f.value; });
    return fetch(LEAD_SUBMIT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        if (!res.ok || !json || json.ok !== true) {
          throw new Error('lead-submit failed: ' + (json && json.error ? json.error : ('HTTP ' + res.status)));
        }
        return json;
      });
    });
  }

  // Sends { email, name, service, downloadUrl, source } to the brochure-email endpoint. Resolves
  // `true`/`false` (never rejects) so callers never need a .catch — a failed email send is a
  // normal, expected outcome to handle in the UI, not an exceptional one.
  function sendBrochureEmail(endpoint, payload) {
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) { return res.json().catch(function () { return {}; }); })
      .then(function (json) { return json && json.ok === true; })
      .catch(function () { return false; });
  }

  function bind(container) {
    if (container.dataset.hsBound === '1') return; // guard against double-binding
    var form = container.querySelector('form');
    if (!form) return;
    var submitBtn = form.querySelector('button[type="submit"]');
    if (!submitBtn) return;
    var guid = container.getAttribute('data-hs-form-guid');
    if (!guid) return; // not configured yet -> keep native Shopify submission

    container.dataset.hsBound = '1';
    var portal = container.getAttribute('data-hs-portal');
    var downloadUrl = container.getAttribute('data-hs-download-url') || '';
    var brochureEndpoint = container.getAttribute('data-hs-brochure-endpoint') || BROCHURE_EMAIL_ENDPOINT;
    var service = container.getAttribute('data-hs-service') || '';
    var source = container.getAttribute('data-hs-source') || document.title;
    var msgProp = container.getAttribute('data-hs-message-property') || 'message';

    // [2026-09-11, L2/L4 fix] The submit button now visibly reflects the constraint the
    // browser's own `required`/`pattern` attributes already enforce on click (see the
    // `!form.checkValidity()` guard a few lines down, unchanged) — greyed out and inert
    // until every required field (name, email, phone, plus any page-specific required
    // field) is actually valid, not just clickable-but-rejected. Every lead form on the
    // site already requires+validates email (type=email) and phone (type=tel + a 7-15
    // digit pattern) via this same markup, so this is a visibility fix on top of
    // validation that was already there, not a new validation rule. Payment/checkout is
    // Shopify-hosted and outside this theme — not addressable here.
    function syncSubmitState() { submitBtn.disabled = !form.checkValidity(); }
    syncSubmitState();
    form.addEventListener('input', syncSubmitState);
    form.addEventListener('change', syncSubmitState);

    // Populated on every submit attempt, read by the retry button below — lets a visitor retry
    // just the email step (lead is already safely in HubSpot) without resubmitting the form.
    var lastAttempt = null;

    function showSuccess(email) {
      toggle(form.querySelector('[data-hs-error]'), false);
      toggle(form.querySelector('[data-hs-email-error]'), false);
      var successEl = form.querySelector('[data-hs-success]');
      var emailSlot = form.querySelector('[data-hs-success-email]');
      if (emailSlot) emailSlot.textContent = email;
      toggle(successEl, true);
    }

    function showEmailError() {
      toggle(form.querySelector('[data-hs-success]'), false);
      toggle(form.querySelector('[data-hs-error]'), false);
      toggle(form.querySelector('[data-hs-email-error]'), true);
    }

    var retryBtn = form.querySelector('[data-hs-retry-email]');
    if (retryBtn) {
      retryBtn.addEventListener('click', function () {
        if (!lastAttempt || retryBtn.disabled) return;
        retryBtn.disabled = true;
        sendBrochureEmail(brochureEndpoint, lastAttempt)
          .then(function (ok) {
            if (ok) {
              showSuccess(lastAttempt.email);
            } else {
              showEmailError(); // stays visible, button re-enabled below, visitor can try again
            }
          })
          .finally(function () { retryBtn.disabled = false; });
      });
    }

    // Capture phase, on the button itself — see "INTERCEPT POINT" in the header comment for why
    // this can't be a form-level 'submit' listener on this store.
    submitBtn.addEventListener('click', function (evt) {
      if (!form.checkValidity()) return; // let native HTML5 validation UI show as usual
      evt.preventDefault();
      submitBtn.disabled = true;

      toggle(form.querySelector('[data-hs-success]'), false);
      toggle(form.querySelector('[data-hs-error]'), false);
      toggle(form.querySelector('[data-hs-email-error]'), false);

      var name = splitName(qs(form, 'contact[name]'));
      var email = qs(form, 'contact[email]');
      var countryCode = qs(form, 'contact[Country code]');
      var phoneRaw = qs(form, 'contact[phone]');
      // De-duplicates a country code the visitor may have typed themselves (with or without
      // "+", with or without a local trunk "0") against the one picked in the selector —
      // see normalizePhone() above. Also strips stray formatting chars, same as before, so
      // HubSpot's strict phone validator doesn't reject spaces/dashes/brackets.
      var phone = normalizePhone(countryCode, phoneRaw);
      var org = qs(form, 'contact[Organisation]');
      var guests = qs(form, 'contact[Group size]');
      var date = qs(form, 'contact[Preferred date]');
      var body = qs(form, 'contact[body]');

      var messageValue = buildMessage(service, source, guests, date, body);
      var fieldValues = [
        { name: 'firstname', value: name.first },
        { name: 'lastname', value: name.last },
        { name: 'email', value: email },
        { name: 'mobilephone', value: phone },
        { name: 'company', value: org },
        { name: msgProp, value: messageValue }
      ];

      sendLeadToHubspot(fieldValues)
        .then(function () {
          // Capture the submitted values before form.reset() clears the inputs — needed both to
          // show "on its way to <email>" and to let the retry button resend without the fields.
          lastAttempt = {
            email: email,
            name: (name.first + ' ' + name.last).trim(),
            service: service,
            downloadUrl: downloadUrl,
            source: source
          };
          form.reset();

          if (!downloadUrl) {
            // Not a brochure form (e.g. contact/enquire/competitions) — unchanged behaviour,
            // no email-delivery step.
            toggle(form.querySelector('[data-hs-success]'), true);
            return;
          }

          // Brochure form: the lead already reached HubSpot at this point (nothing below can
          // lose it). Deliver the file by email before declaring success to the visitor — a
          // "check your inbox" message that turns out to be false is worse than a half-second
          // wait, and a failed send here must never be silent (falls to showEmailError with a
          // retry, per Marcello's fallback requirement).
          return sendBrochureEmail(brochureEndpoint, lastAttempt).then(function (ok) {
            if (ok) {
              showSuccess(email);
            } else {
              showEmailError();
            }
          });
        })
        .catch(function (err) {
          if (window.console) console.error('[hs-lead-submit]', err);
          toggle(form.querySelector('[data-hs-error]'), true);
        })
        .finally(function () {
          // Was an unconditional `= false` — now defers to the same validity check as
          // above, so a successful submit (form.reset() just cleared every field) leaves
          // the button correctly disabled again instead of re-enabling it over an empty
          // form; an error leaves the still-filled, still-valid fields re-enabling it.
          syncSubmitState();
        });
    }, true);
  }

  function init() {
    var containers = document.querySelectorAll('[data-hs-form]');
    for (var i = 0; i < containers.length; i++) bind(containers[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
